import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { exportChangeOrders } from '@/utils/exportUtils';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CSI_DIVISIONS = [
  '01 - General Requirements', '02 - Existing Conditions', '03 - Concrete', '04 - Masonry',
  '05 - Metals', '06 - Wood, Plastics, Composites', '07 - Thermal & Moisture Protection',
  '08 - Openings', '09 - Finishes', '10 - Specialties', '11 - Equipment', '12 - Furnishings',
  '13 - Special Construction', '14 - Conveying Equipment', '21 - Fire Suppression',
  '22 - Plumbing', '23 - HVAC', '26 - Electrical', '27 - Communications', '28 - Electronic Safety',
  '31 - Earthwork', '32 - Exterior Improvements', '33 - Utilities', 'Other'
];

export const ChangeOrdersTabEnhanced = ({ projectId }) => {
  const [changeOrders, setChangeOrders] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedCO, setSelectedCO] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [actionStatus, setActionStatus] = useState('');
  const [actionComments, setActionComments] = useState('');
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({ title: '', description: '', amount: '', contractor_name: '', csi_division: '', assigned_to: '' });
  const { user } = useAuth();

  useEffect(() => {
    fetchChangeOrders();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      // Use project-specific users endpoint for non-admin users
      const response = await axios.get(`${API}/projects/${projectId}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchChangeOrders = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/change-orders`);
      setChangeOrders(response.data);
    } catch (error) {
      toast.error('Failed to load change orders');
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;
    setUploadingFile(true);
    const fileData = new FormData();
    fileData.append('file', selectedFile);
    fileData.append('document_type', 'change_orders');
    fileData.append('project_id', projectId);
    try {
      const response = await axios.post(`${API}/upload`, fileData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data.file_url;
    } catch (error) {
      toast.error('File upload failed');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const createChangeOrder = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/projects/${projectId}/change-orders`, {
        title: formData.title,
        description: formData.description,
        amount: parseFloat(formData.amount),
        contractor_name: formData.contractor_name,
        csi_division: formData.csi_division,
        assigned_to: formData.assigned_to
      });
      const coId = response.data.id;
      if (selectedFile) {
        const fileUrl = await uploadFile();
        if (fileUrl) {
          await axios.patch(`${API}/change-orders/${coId}`, { file_url: fileUrl });
        }
      }
      toast.success('Change order created!');
      setShowCreateModal(false);
      setFormData({ title: '', description: '', amount: '', contractor_name: '', csi_division: '', assigned_to: '' });
      setSelectedFile(null);
      fetchChangeOrders();
    } catch (error) {
      toast.error('Failed to create change order');
    }
  };

  const openActionModal = (co) => {
    setSelectedCO(co);
    setActionStatus(co.status);
    setActionComments(co.review_comments || '');
    setShowActionModal(true);
  };

  const submitAction = async () => {
    try {
      await axios.patch(`${API}/change-orders/${selectedCO.id}`, {
        status: actionStatus,
        review_comments: actionComments
      });
      toast.success(actionStatus === 'approved' ? 'Change order approved! Budget updated automatically.' : 'Change order updated');
      setShowActionModal(false);
      fetchChangeOrders();
    } catch (error) {
      toast.error('Failed to update change order');
    }
  };

  const formatDateMMDDYY = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };

  return (
    <div className="space-y-4" data-testid="change-orders-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Change Orders</h2>
          <p className="text-sm text-slate-600 mt-1">Track project changes and cost adjustments</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportChangeOrders(changeOrders, 'excel')} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm" disabled={changeOrders.length === 0}>Export Excel</button>
          <button onClick={() => exportChangeOrders(changeOrders, 'pdf')} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm" disabled={changeOrders.length === 0}>Export PDF</button>
          <button onClick={() => setShowCreateModal(true)} className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800" data-testid="create-change-order-button">Create Change Order</button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-auto">
        <table className="w-full" data-testid="change-orders-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">CO #</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Division</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Contractor</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned To</th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Approved Date</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {changeOrders.map((co, index) => (
              <tr key={co.id} className="border-b hover:bg-slate-50" data-testid={`co-row-${co.id}`}>
                <td className="p-4 font-mono font-medium">CO-{String(index + 1).padStart(3, '0')}</td>
                <td className="p-4 font-medium">{co.title}</td>
                <td className="p-4 text-sm">{co.csi_division || '-'}</td>
                <td className="p-4">{co.contractor_name}</td>
                <td className="p-4 text-sm">{co.assigned_to_name || 'Unassigned'}</td>
                <td className="p-4 text-right font-mono font-semibold">${co.amount.toLocaleString()}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    co.status === 'approved' ? 'bg-green-100 text-green-700' :
                    co.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    co.status === 'resubmit' ? 'bg-orange-100 text-orange-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {co.status}
                  </span>
                </td>
                <td className="p-4 text-sm" data-testid={`co-approved-date-${co.id}`}>
                  {formatDateMMDDYY(co.approved_date)}
                </td>
                <td className="p-4">
                  {/* Review button - only for assignee or admin */}
                  {(user?.role === 'admin' || user?.id === co.assigned_to) && (
                    <button 
                      onClick={() => openActionModal(co)} 
                      className="text-sm text-blue-600 hover:underline font-medium"
                      data-testid={`review-co-${co.id}`}
                    >
                      Review
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Create Change Order</h2>
            <form onSubmit={createChangeOrder} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required placeholder="e.g., Additional HVAC Units" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" rows="4" required placeholder="Detailed description..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">CSI Division *</label>
                  <select value={formData.csi_division} onChange={(e) => setFormData({...formData, csi_division: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required>
                    <option value="">Select division</option>
                    {CSI_DIVISIONS.map(div => <option key={div} value={div}>{div}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contractor *</label>
                  <input type="text" value={formData.contractor_name} onChange={(e) => setFormData({...formData, contractor_name: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Amount ($) *</label>
                  <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Assign To *</label>
                  <select value={formData.assigned_to} onChange={(e) => setFormData({...formData, assigned_to: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required data-testid="co-assign-select">
                    <option value="">Select user</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Attach File</label>
                  <input type="file" onChange={handleFileSelect} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" disabled={uploadingFile} className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800">{uploadingFile ? 'Uploading...' : 'Create'}</button>
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showActionModal && selectedCO && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowActionModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Review Change Order</h2>
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-2">{selectedCO.title}</h3>
              <p className="text-sm text-slate-600 mb-2">{selectedCO.description}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-semibold">Division:</span> {selectedCO.csi_division}</div>
                <div><span className="font-semibold">Contractor:</span> {selectedCO.contractor_name}</div>
                <div><span className="font-semibold">Amount:</span> ${selectedCO.amount.toLocaleString()}</div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Decision *</label>
                <select value={actionStatus} onChange={(e) => setActionStatus(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md" required>
                  <option value="">Select decision</option>
                  <option value="approved">Approved (Will update budget)</option>
                  <option value="rejected">Rejected</option>
                  <option value="resubmit">Resubmit Required</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Comments</label>
                <textarea value={actionComments} onChange={(e) => setActionComments(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md" rows="3" placeholder="Add review comments..." />
              </div>
              <div className="flex gap-3">
                <button onClick={submitAction} className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800">Submit Review</button>
                <button onClick={() => setShowActionModal(false)} className="flex-1 border py-2 rounded-md">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};